import { Component, createRef } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import * as XLSX from 'xlsx';

// ── Brand palette (matches dashboard / CSR / Donor management) ────
const BRAND = {
  red:    "#dc3545",
  ink:    "#1B1B1F",
  muted:  "#6c757d",
  bg:     "#f8f9fa",
  border: "#eef0f2",
};

class StatCard extends Component {
  render() {
    const { icon, value, label, tint } = this.props;
    return (
      <div className="col-md-4">
        <div
          className="d-flex align-items-center gap-3 p-3 h-100"
          style={{
            background: "#fff",
            border: `1px solid ${BRAND.border}`,
            borderRadius: 14,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ width: 46, height: 46, borderRadius: 12, background: tint, flexShrink: 0 }}
          >
            <i className={`bi ${icon}`} style={{ fontSize: 20, color: BRAND.red }} />
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: BRAND.ink, lineHeight: 1.1 }}>
              {value}
            </div>
            <div className="text-muted small">{label}</div>
          </div>
        </div>
      </div>
    );
  }
}

class Cities extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cities: [],
      districts: [],
      loading: true,
      submitting: false,
      importing: false,
      error: "",
      success: "",
      showAdd: false,
      newCity: "",
      newDistrictId: "",
      showEdit: false,
      editId: null,
      editName: "",
      editStatus: "active",
      editDistrictId: "",
      deleteId: null,
    };
    this.fileInputRef = createRef();
  }

  componentDidMount() {
    this.fetchCities();
    this.fetchDistricts();
  }

  showAlert(type, msg) {
    this.setState({ [type]: msg });
    setTimeout(() => this.setState({ [type]: "" }), 3000);
  }
  formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d)) return "—";
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = String(d.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }
  async fetchCities() {
    this.setState({ loading: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cities`, { credentials: "include" });
      const data = await res.json();
      this.setState({ cities: data.data || [], loading: false });
    } catch {
      this.setState({ loading: false });
    }
  }

  async fetchDistricts() {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/districts`, { credentials: "include" });
      const data = await res.json();
      this.setState({ districts: data.data || [] });
    } catch { }
  }

  // ── Export ────────────────────────────────────────────────
  handleExport() {
    const { cities } = this.state;
    if (!cities.length) return this.showAlert("error", "No cities to export.");

    const header = "name,district,status";
    const rows = cities.map(c => `${c.name},${c.district_name || ""},${c.status}`);
    const csv = [header, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cities.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Import ────────────────────────────────────────────────
  handleImportClick() {
    this.fileInputRef.current.value = "";
    this.fileInputRef.current.click();
  }

  async handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith(".csv");
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

    if (!isCSV && !isExcel) {
      return this.showAlert("error", "Please select a .csv, .xlsx, or .xls file.");
    }

    this.setState({ importing: true });
    try {
      let cities = [];

      if (isCSV) {
        const text = await file.text();
        const lines = text.trim().split("\n").filter(Boolean);
        const dataLines = lines[0].toLowerCase().startsWith("name")
          ? lines.slice(1)
          : lines;

        cities = dataLines.map(line => {
          const [name = "", district = "", status = "active"] = line.split(",").map(s => s.trim());
          return {
            name,
            district,
            status: ["active", "inactive"].includes(status.toLowerCase())
              ? status.toLowerCase()
              : "active",
          };
        }).filter(c => c.name);

      } else {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        cities = rows.map(row => {
          const name = String(row["name"] || row["Name"] || "").trim();
          const district = String(row["district"] || row["District"] || "").trim();
          const status = String(row["status"] || row["Status"] || "active").trim().toLowerCase();
          return {
            name,
            district,
            status: ["active", "inactive"].includes(status) ? status : "active",
          };
        }).filter(c => c.name);
      }

      if (!cities.length) {
        return this.showAlert("error", "No valid city rows found in the file.");
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cities/import`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cities }),
      });
      const data = await res.json();

      if (res.ok) {
        this.showAlert("success", data.message || "Cities imported successfully.");
        this.fetchCities();
      } else {
        this.showAlert("error", data.message || "Import failed.");
      }
    } catch {
      this.showAlert("error", "Failed to read or upload file.");
    } finally {
      this.setState({ importing: false });
    }
  }

  // ── Add / Update / Delete ────────────────────────────────
  async handleAdd(e) {
    e.preventDefault();
    const { newCity, newDistrictId } = this.state;
    if (!newCity.trim()) return this.showAlert("error", "City name is required.");
    this.setState({ submitting: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cities`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newCity.trim(),
          district_id: newDistrictId || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        this.setState({ newCity: "", newDistrictId: "", showAdd: false });
        this.showAlert("success", "City added successfully.");
        this.fetchCities();
      } else {
        this.showAlert("error", data.message);
      }
    } catch {
      this.showAlert("error", "Something went wrong.");
    } finally {
      this.setState({ submitting: false });
    }
  }

  async handleUpdate(e) {
    e.preventDefault();
    const { editId, editName, editStatus, editDistrictId } = this.state;
    if (!editName.trim()) return this.showAlert("error", "City name is required.");
    this.setState({ submitting: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cities/${editId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editName,
          status: editStatus,
          district_id: editDistrictId || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        this.setState({ showEdit: false, editId: null });
        this.showAlert("success", "City updated successfully.");
        this.fetchCities();
      } else {
        this.showAlert("error", data.message);
      }
    } catch {
      this.showAlert("error", "Something went wrong.");
    } finally {
      this.setState({ submitting: false });
    }
  }

  async handleDelete(id) {
    this.setState({ submitting: true });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cities/${id}`, { method: "DELETE", credentials: "include", });
      const data = await res.json();
      if (res.ok) {
        this.setState({ deleteId: null });
        this.showAlert("success", "City deleted.");
        this.fetchCities();
      } else {
        this.showAlert("error", data.message);
      }
    } catch {
      this.showAlert("error", "Something went wrong.");
    } finally {
      this.setState({ submitting: false });
    }
  }

  openEdit(city) {
    this.setState({
      showEdit: true,
      editId: city.id,
      editName: city.name,
      editStatus: city.status,
      editDistrictId: city.district_id || "",
    });
  }

  // ── Modal ─────────────────────────────────────────────────
  renderModal({ title, onClose, onSubmit, children, submitting }) {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }} onClick={onClose}>
        <div style={{
          background: "#fff", borderRadius: 14,
          width: "100%", maxWidth: 440,
          boxShadow: "0 20px 50px rgba(0,0,0,0.22)",
          overflow: "hidden",
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            padding: "18px 22px", borderBottom: `1px solid ${BRAND.border}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <h6 style={{ margin: 0, fontWeight: 700 }}>{title}</h6>
            <button onClick={onClose} style={{
              background: "none", border: "none", fontSize: 20,
              cursor: "pointer", color: "#888", lineHeight: 1,
            }}>×</button>
          </div>
          <form onSubmit={onSubmit}>
            <div style={{ padding: 22 }}>{children}</div>
            <div style={{
              padding: "14px 22px", borderTop: `1px solid ${BRAND.border}`,
              display: "flex", justifyContent: "flex-end", gap: 10,
              background: BRAND.bg,
            }}>
              <button type="button" onClick={onClose} style={{
                padding: "8px 18px", borderRadius: 8, border: "1px solid #ddd",
                background: "#fff", fontWeight: 600, cursor: "pointer",
              }}>Cancel</button>
              <button type="submit" disabled={submitting} style={{
                padding: "8px 20px", borderRadius: 8, border: "none",
                background: BRAND.red, color: "#fff", fontWeight: 600, cursor: "pointer",
              }}>
                {submitting ? <span className="spinner-border spinner-border-sm" /> : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  render() {
    const {
      cities, districts, loading, submitting, importing,
      error, success,
      showAdd, newCity, newDistrictId,
      showEdit, editName, editStatus, editDistrictId,
      deleteId,
    } = this.state;

    const activeCount = cities.filter(c => c.status === "active").length;
    const inactiveCount = cities.length - activeCount;

    return (
      <div className="pt-5 mt-4" style={{ background: BRAND.bg, minHeight: "100vh" }}>
        <div className="container-fluid py-4 px-4 px-lg-5">

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">City Management</h4>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <input
                ref={this.fileInputRef}
                type="file"
                accept=".csv, .xlsx, .xls"
                style={{ display: "none" }}
                onChange={e => this.handleFileChange(e)}
              />

              <button
                onClick={() => this.handleImportClick()}
                disabled={importing}
                style={{
                  background: "#fff", color: BRAND.red,
                  border: `1.5px solid ${BRAND.red}`,
                  borderRadius: 8, padding: "9px 16px",
                  fontWeight: 600, cursor: "pointer", fontSize: 14,
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {importing
                  ? <span className="spinner-border spinner-border-sm text-danger" />
                  : <i className="bi bi-upload" />
                }
                Import
              </button>

              <button
                onClick={() => this.handleExport()}
                style={{
                  background: "#fff", color: "#198754",
                  border: "1.5px solid #198754",
                  borderRadius: 8, padding: "9px 16px",
                  fontWeight: 600, cursor: "pointer", fontSize: 14,
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                <i className="bi bi-download" /> Export
              </button>

              <button
                onClick={() => this.setState({ showAdd: true, newCity: "", newDistrictId: "" })}
                style={{
                  background: BRAND.red, color: "#fff", border: "none",
                  borderRadius: 8, padding: "9px 20px",
                  fontWeight: 600, cursor: "pointer", fontSize: 14,
                  boxShadow: "0 4px 12px rgba(220,53,69,0.25)",
                }}
              >
                <i className="bi bi-plus-lg me-1" /> Add City
              </button>
            </div>
          </div>

          {error && <div className="alert alert-danger  py-2 small">⚠️ {error}</div>}
          {success && <div className="alert alert-success py-2 small">✅ {success}</div>}

      

          <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">All Cities</h6>
                <span className="badge" style={{ background: BRAND.red, borderRadius: 20, padding: "6px 12px" }}>
                  {cities.length} total
                </span>
              </div>
              {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-danger" /></div>
              ) : cities.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-buildings" style={{ fontSize: 40, color: "#ccc" }} />
                  <p className="mt-2 mb-0">No cities added yet.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${BRAND.border}` }}>
                        <th className="text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>#</th>
                        <th className="text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>City Name</th>
                        <th className="text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>District</th>
                        <th className="text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>Status</th>
                        <th className="text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>Added On</th>
                        <th className="text-muted small text-uppercase" style={{ letterSpacing: "0.04em" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cities.map((city, i) => (
                        <tr key={city.id} style={{ borderBottom: `1px solid ${BRAND.border}` }}>
                          <td className="text-muted small">{i + 1}</td>
                          <td className="fw-semibold">{city.name}</td>
                          <td className="text-muted small">
                            {city.district_name
                              ? city.district_name
                              : <span className="fst-italic">— none —</span>}
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                background: city.status === "active" ? "#198754" : "#6c757d",
                                borderRadius: 20, padding: "5px 12px", fontWeight: 500,
                              }}
                            >
                              {city.status.charAt(0).toUpperCase() + city.status.slice(1)}
                            </span>
                          </td>
                          <td className="text-muted small">{this.formatDate(city.created_at)}</td>
                          <td>
                            {deleteId === city.id ? (
                              <div className="d-flex gap-2 align-items-center">
                                <span className="text-danger small fw-semibold">Delete?</span>
                                <button className="btn btn-danger btn-sm"
                                  onClick={() => this.handleDelete(city.id)}
                                  disabled={submitting}>Yes</button>
                                <button className="btn btn-secondary btn-sm"
                                  onClick={() => this.setState({ deleteId: null })}>No</button>
                              </div>
                            ) : (
                              <div className="d-flex gap-3">
                                <button className="btn btn-link p-0 text-secondary"
                                  onClick={() => this.openEdit(city)} title="Edit" style={{ fontSize: 15 }}>
                                  <i className="bi bi-pencil" />
                                </button>
                                <button className="btn btn-link p-0 text-danger"
                                  onClick={() => this.setState({ deleteId: city.id })} title="Delete" style={{ fontSize: 15 }}>
                                  <i className="bi bi-trash" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Modal */}
        {showAdd && this.renderModal({
          title: "Add New City", submitting,
          onClose: () => this.setState({ showAdd: false }),
          onSubmit: e => this.handleAdd(e),
          children: (
            <>
              <div className="mb-3">
                <label className="form-label fw-semibold small">City Name</label>
                <input type="text" className="form-control" placeholder="e.g. Rawalpindi"
                  value={newCity} onChange={e => this.setState({ newCity: e.target.value })} autoFocus />
              </div>
              <div>
                <label className="form-label fw-semibold small">District</label>
                <select className="form-select" value={newDistrictId}
                  onChange={e => this.setState({ newDistrictId: e.target.value })}>
                  <option value="">No district</option>
                  {districts.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            </>
          ),
        })}

        {/* Edit Modal */}
        {showEdit && this.renderModal({
          title: "Edit City", submitting,
          onClose: () => this.setState({ showEdit: false }),
          onSubmit: e => this.handleUpdate(e),
          children: (
            <>
              <div className="mb-3">
                <label className="form-label fw-semibold small">City Name</label>
                <input type="text" className="form-control" value={editName}
                  onChange={e => this.setState({ editName: e.target.value })} autoFocus />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold small">District</label>
                <select className="form-select" value={editDistrictId}
                  onChange={e => this.setState({ editDistrictId: e.target.value })}>
                  <option value="">No district</option>
                  {districts.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label fw-semibold small">Status</label>
                <select className="form-select" value={editStatus}
                  onChange={e => this.setState({ editStatus: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </>
          ),
        })}
      </div>
    );
  }
}

export default Cities;